import React from 'react';
import { CheckCircle2, Award, Users, Shield, Star, Phone, MessageCircle } from 'lucide-react';

export const About = () => {
  const features = [
    { icon: CheckCircle2, title: 'Discrétion absolue garantie' },
    { icon: Award, title: 'Résultats rapides en 3-7 jours' },
    { icon: Users, title: 'Travail sérieux et personnalisé' },
    { icon: Shield, title: 'Facilité de paiement' },
  ];

  return (
    <section id="about" className="py-14 bg-black relative overflow-hidden">
      {/* Subtle Pattern Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(45deg, rgb(212, 168, 67) 25%, transparent 25%), linear-gradient(-45deg, rgb(212, 168, 67) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgb(212, 168, 67) 75%), linear-gradient(-45deg, transparent 75%, rgb(212, 168, 67) 75%)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0px 0px, 0px 10px, 10px -10px, -10px 0px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: Text Content */}
          <div>
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">À Propos du Grand Marabout</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-4 mb-8" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              <span className="text-amber-400">Maître Diamma</span> : Marabout Africain Sérieux à Genève
              <span className="block text-xl sm:text-2xl text-amber-200/70 font-light mt-2">N°1 du Retour Affectif - 30 ans d'expérience</span>
            </h2>
            <div className="space-y-6">
              <p className="text-amber-100/70 leading-relaxed text-lg">
                Le grand marabout africain <strong className="text-white">Maître Diamma</strong> est reconnu à Genève, héritier d'une longue lignée de guérisseurs traditionnels originaires d'Afrique de l'Ouest. Fort de plus de 30 ans d'expérience, il met son don exceptionnel au service de tous ceux qui souffrent.
              </p>
              <p className="text-amber-100/60 leading-relaxed">
                Formé aux arts divinatoires et aux rituels ancestraux africains dès son plus jeune âge, Maître Diamma possède des pouvoirs extraordinaires transmis de génération en génération. Installé à Genève depuis de nombreuses années, sa réputation s'étend dans toute la Suisse.
              </p>
              <p className="text-amber-100/60 leading-relaxed">
                Face aux problèmes sentimentaux, familiaux, professionnels ou de santé, ce marabout sérieux à Genève apporte des solutions rapides et efficaces. Spécialiste reconnu du retour affectif et du désenvoûtement, il garantit des résultats concrets en 3 à 7 jours.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                    <Icon className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <span className="text-amber-100/80 text-sm">{feature.title}</span>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href="tel:+41767836186" className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-6 py-3 rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25" data-testid="about-call-btn">
                <Phone className="w-5 h-5" />
                <span>+41 76 783 61 86</span>
              </a>
              <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-lg transition-all" data-testid="about-whatsapp-btn">
                <MessageCircle className="w-5 h-5" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Right: Stats & Info */}
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '30+', label: "Années d'expérience" },
                { value: '800+', label: 'Clients satisfaits' },
                { value: '4.9/5', label: 'Note moyenne' },
                { value: '100%', label: 'Discrétion' },
              ].map((stat) => (
                <div key={stat.label} className="bg-neutral-900/50 border border-amber-500/20 rounded-xl p-6 text-center hover:border-amber-500/40 transition-all">
                  <div className="text-3xl font-bold text-amber-400 mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>{stat.value}</div>
                  <p className="text-amber-200/60 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Quote Card */}
            <div className="bg-gradient-to-br from-neutral-900 to-neutral-950 border border-amber-500/20 rounded-2xl p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/30 to-amber-600/10 flex items-center justify-center">
                  <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Maître Diamma</h3>
                  <p className="text-amber-200/60 text-sm">Grand Marabout Africain</p>
                  <p className="text-amber-200/50 text-xs">Genève, Suisse</p>
                </div>
              </div>
              <blockquote className="text-lg italic text-amber-100/70 border-l-4 border-amber-500 pl-4">
                "Votre bonheur est ma mission. Je mets mes 30 ans d'expérience et mes dons au service de votre bien-être."
              </blockquote>
            </div>

            {/* Specialties */}
            <div className="bg-neutral-900/50 border border-amber-500/20 rounded-xl p-6">
              <h4 className="text-white font-semibold mb-4">Spécialités :</h4>
              <div className="flex flex-wrap gap-2">
                {['Retour Affectif', 'Voyance', 'Guérison', 'Protection', 'Chance', 'Réussite'].map((s) => (
                  <span key={s} className="text-xs text-amber-200/70 bg-amber-500/5 px-3 py-1.5 rounded-full border border-amber-500/20 hover:bg-amber-500/15 hover:text-amber-300 transition-all">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
